import { indexOf, sortBy } from 'lodash';
import { translateBlock, translateBlockValue } from 'i18n';
import { ColDef } from 'ag-grid-community';
import { InscriptionWithMetadata } from 'routes/BudgetModule/config/types';
import {
    PROJECT_ID,
    DIVIDED_IMPUTATIONS_COLUMN_FIELDS,
    ORG_CHART_FINANCE_FIELD,
    NATURE_LABEL_FIELD,
    FUNCTION_LABEL_FIELD,
    ANTENNA_NAME_FIELD,
    ANALYTIC_FIELD,
    LINE_NAME_FIELD,
    OPERATION_NAME_FIELD,
    BUDGET_LINE_CODE,
    ANALYTIC_NAME_FIELD,
    BUDGET_NAME_FIELD,
    INSCRIPTION_INVESTMENT_OPERATING,
    REAL_ORDER_FIELD,
    CHAPTER_LABEL_FIELD,
    DIRECTION_LABEL,
    BUDGET_LINE_PROGRAM_NAME,
    ENVELOPPE_LABEL,
    BUDGET_LINE_PROGRAM_CODE,
    ENVELOPPE_CODE,
    ANALYTIC_NATURE_NAME,
    ANALYTIC_NATURE_CODE,
    IS_REJECTED,
} from 'routes/BudgetModule/config/constants';
import { GESTIONNAIRE_FIELD } from 'business/constants';
import { getInscriptionGridPhaseColumn } from 'routes/BudgetModule/components/shared/InscriptionsGrid/gridHelpers';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import {
    getOriginalOrCurrentInscriptionValue,
    inscriptionTextComparator,
} from 'routes/BudgetModule/components/shared/InscriptionsGrid/dataHelpers';

export const getOrderedColumns = (columns: ColDef[]): ColDef[] => {
    const order = [
        'details',
        'openButton',
        'status',
        'originalInscription',
        'amount',
        'comment',
        ORG_CHART_FINANCE_FIELD,
        GESTIONNAIRE_FIELD,
        BUDGET_NAME_FIELD,
        INSCRIPTION_INVESTMENT_OPERATING,
        DIRECTION_LABEL,
        CHAPTER_LABEL_FIELD,
        NATURE_LABEL_FIELD,
        FUNCTION_LABEL_FIELD,
        ANTENNA_NAME_FIELD,
        OPERATION_NAME_FIELD,
        ANALYTIC_FIELD,
        ANALYTIC_NAME_FIELD,
        LINE_NAME_FIELD,
        BUDGET_LINE_CODE,
        PROJECT_ID,
        REAL_ORDER_FIELD,
        BUDGET_LINE_PROGRAM_NAME,
        BUDGET_LINE_PROGRAM_CODE,
        ENVELOPPE_LABEL,
        ENVELOPPE_CODE,
        ANALYTIC_NATURE_NAME,
        ANALYTIC_NATURE_CODE,
        'imputation',
        'phase',
        'actions',
    ];
    return sortBy(columns, (obj) => indexOf(order, obj.field));
};

/**
 * Build and returns the list of inscriptions fields to be displayed, relevent for all integrated softwares.
 */
const getImputationColumnFields = (
    softwaresIntegrations: string[],
): readonly (keyof InscriptionT)[] => {
    const matchingImputationColumnFields = softwaresIntegrations.reduce(
        (acc: (keyof InscriptionT)[], integration: string) => {
            acc.push(
                ...(DIVIDED_IMPUTATIONS_COLUMN_FIELDS[integration] ||
                    DIVIDED_IMPUTATIONS_COLUMN_FIELDS.DEFAULT),
            );
            return acc;
        },
        [],
    );

    return matchingImputationColumnFields.filter(
        (n, i) => matchingImputationColumnFields.indexOf(n) === i,
    );
};

export const getAllInscriptionsGridColumns = (
    t: TranslationT,
    softwaresIntegrations: string[],
): ColDef[] => {
    const normalStyle = { fontWeight: 400 };
    const imputationColumnFields = getImputationColumnFields(
        softwaresIntegrations,
    );

    const dividedImputationColumns = imputationColumnFields.map(
        (columnField) => ({
            field: columnField,
            headerName: translateBlock(columnField, true),
            sortable: true,
            resizable: true,

            valueGetter: ({ data }: { data: InscriptionWithMetadata }) => {
                const value = data?.inscription[columnField];
                return typeof value === 'string'
                    ? translateBlockValue(value)
                    : value;
            },
            width: 150,
            cellRenderer: 'textHoverTooltip',
            cellRendererParams: {
                customStyle: normalStyle,
            },
            filter: 'agTextColumnFilter',
            floatingFilterComponent: 'textFloatingFilter',
            enableRowGroup: true,
        }),
    );
    const openButtonColumn = {
        field: 'openButton',
        headerName: '',
        sortable: false,
        resizable: true,
        width: 40,
        cellRenderer: 'openButtonCellRenderer',
        suppressHeaderMenuButton: true,
        editable: false,
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        suppressMovable: true,
    };
    const phaseColumn = getInscriptionGridPhaseColumn(t);
    const originalInscriptionColumn = {
        field: 'originalInscription',
        headerName: t('budgetProposal.yearInscription'),
        sortable: true,
        valueGetter: ({ data }: { data: InscriptionWithMetadata | null }) => {
            if (!data) return null;
            return {
                text: getOriginalOrCurrentInscriptionValue(
                    data.inscription,
                    // @ts-expect-error explo-AMP-type-fix (Shortcut ID : 28345)
                    data.budgetProposalPhase,
                    t,
                ),
                isRejected: data.inscription[IS_REJECTED],
            };
        },
        valueFormatter: ({ data }: { data?: InscriptionWithMetadata }) => {
            if (!data) return '';
            return getOriginalOrCurrentInscriptionValue(
                data.inscription,
                // @ts-expect-error explo-AMP-type-fix (Shortcut ID : 28345)
                data.budgetProposalPhase,
                t,
            );
        },
        cellRenderer: 'arbitrageInscriptionCellRenderer',
        comparator: inscriptionTextComparator,
        filter: 'agTextColumnFilter',
        floatingFilterComponent: 'textFloatingFilter',
        filterValueGetter: ({
            data,
        }: {
            data: InscriptionWithMetadata | null;
        }) => {
            if (!data) return null;
            return getOriginalOrCurrentInscriptionValue(
                data.inscription,
                // @ts-expect-error explo-AMP-type-fix (Shortcut ID : 28345)
                data.budgetProposalPhase,
                t,
            );
        },
    };
    return [
        ...dividedImputationColumns,
        originalInscriptionColumn,
        openButtonColumn,
        phaseColumn,
    ];
};
